import React, { useState, useEffect } from 'react';
import { User } from '../types';

interface LoginProps {
  onLoginSuccess: (user: User) => void;
  workerUrl: string;
  workerSecret: string;
  onUpdateConnection: (url: string, secret: string) => void;
}

// Hardcoded system defaults for "just works" experience
const INITIAL_ADMIN: User = {
  id: 'admin-kyle',
  name: 'Kyle Admin',
  email: 'kyle@surrogacy.is',
  password: 'Sandieg00!!',
  role: 'admin'
};

export const Login: React.FC<LoginProps> = ({ onLoginSuccess, workerUrl, workerSecret, onUpdateConnection }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  const [localUrl, setLocalUrl] = useState(workerUrl || localStorage.getItem('si_worker_url') || window.location.origin);
  const [localSecret, setLocalSecret] = useState(workerSecret || localStorage.getItem('si_worker_secret') || 'Ambasador_Secret_2024');

  useEffect(() => {
    localStorage.setItem('si_worker_url', localUrl);
    localStorage.setItem('si_worker_secret', localSecret);
    onUpdateConnection(localUrl, localSecret);
  }, [localUrl, localSecret, onUpdateConnection]);

  const runSilentSetup = async (baseUrl: string) => {
    try {
      await fetch(`${baseUrl}/setup`, {
        method: 'POST',
        headers: { 
          'Authorization': localSecret,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ mode: 'repair' })
      });
    } catch (e) {
      console.warn("Background initialization unreachable - using local mode.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsAuthenticating(true);

    const workerBase = localUrl.replace(/\/+$/, '');

    try {
      // 1. Check local hardcoded admin first for instant access
      if (email === INITIAL_ADMIN.email && password === INITIAL_ADMIN.password) {
        onLoginSuccess(INITIAL_ADMIN);
        // Fire off setup in background if it's the first run
        runSilentSetup(workerBase);
        return;
      }

      // 2. Try to verify with the remote database
      await runSilentSetup(workerBase);

      const res = await fetch(`${workerBase}/users`, {
        headers: { 'Authorization': localSecret }
      });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const users: User[] = await res.json();
      const user = users.find(u => u.email === email && u.password === password);

      if (user) {
        onLoginSuccess(user);
      } else {
        setError('Incorrect email or password.');
      }
    } catch (err: any) {
      console.error('Remote Login Failure:', err);
      // If the remote server is a 404 or unreachable, we rely solely on hardcoded admin
      if (email === INITIAL_ADMIN.email && password === INITIAL_ADMIN.password) {
        onLoginSuccess(INITIAL_ADMIN);
      } else {
        setError(`System offline. Please check your credentials or contact admin.`);
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="min-h-screen bg-purple-50 flex items-center justify-center p-6 font-sans">
      <div className="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-10 md:p-12 border border-gray-100 animate-in fade-in zoom-in duration-500">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-purple-600 rounded-3xl flex items-center justify-center text-white shadow-xl shadow-purple-200 mx-auto mb-6 group hover:rotate-6 transition-all">
            <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-purple-900 tracking-tight leading-none mb-2">Portal Access</h1>
          <p className="text-[10px] font-black uppercase tracking-widest text-purple-400">Surrogacy Is Ambassador Onboarding</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-600 text-[9px] font-bold uppercase tracking-widest p-4 rounded-2xl mb-6 text-center animate-in fade-in">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Work Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="kyle@surrogacy.is"
              disabled={isAuthenticating}
              className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all placeholder:text-gray-300 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              disabled={isAuthenticating}
              className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all placeholder:text-gray-300 disabled:opacity-50"
            />
          </div>
          <button
            type="submit"
            disabled={isAuthenticating}
            className="w-full bg-purple-600 text-white rounded-full py-5 text-[11px] font-black uppercase tracking-widest shadow-xl shadow-purple-200 hover:bg-purple-700 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 mt-4 disabled:opacity-50"
          >
            {isAuthenticating ? 'Authenticating...' : 'Authenticate Portal'}
          </button>
        </form>

        <div className="mt-12 text-center">
          <p className="text-[9px] text-gray-400 font-medium leading-relaxed">
            Authorized Personnel Only. <br/> 
            Session traffic is monitored and encrypted.
          </p>
        </div>
      </div>
    </div>
  );
};